import ApiService from "./ApiService";
import Logger from "./Logger";

const StatsWordWheelService = {
    /**
     * GET /v1/stats/wordwheel/overview
     * @returns {Promise<{ byLanguage: Object }>}
     */
    getOverviewStats: async () => {
        try {
            return await ApiService.getData("/v1/stats/wordwheel/overview");
        } catch (error) {
            Logger.error("Error fetching word wheel overview stats:", error);
            throw error;
        }
    },

    /**
     * GET /v1/stats/wordwheel/by-character-count?language=english
     * @returns {Promise<{ groups: Array, language: string }>}
     */
    statsByCharacterCount: async (language = "english") => {
        try {
            const params = {};
            if (language) params.language = language;
            return await ApiService.getData("/v1/stats/wordwheel/by-character-count", params);
        } catch (error) {
            Logger.error("Error fetching word wheel stats by character count:", error);
            throw error;
        }
    },

    /**
     * GET /v1/stats/wordwheel/user-plays?start=&end=&language=&season=&includeTest=false&excludeStaff=true
     * Player activity in a window: distinct players, plays per day, puzzle levels played,
     * and the journey levels reached in the season. Start / end are epoch seconds.
     * @returns {Promise<{ totals: Object, daily: Array, byLevelBand: Array, topLevels: Array, journey: Object }>}
     */
    userPlayStats: async ({
        start,
        end,
        language = "english",
        season,
        includeTest = false,
        excludeStaff = true,
    } = {}) => {
        try {
            // ApiService drops falsy values from the query string — send booleans as explicit strings.
            const params = {
                includeTest: includeTest ? "true" : "false",
                excludeStaff: excludeStaff ? "true" : "false",
            };
            if (start) params.start = start;
            if (end) params.end = end;
            if (language) params.language = language;
            if (season) params.season = season;
            return await ApiService.getData("/v1/stats/wordwheel/user-plays", params);
        } catch (error) {
            Logger.error("Error fetching word wheel user play stats:", error);
            throw error;
        }
    },

    /**
     * GET /v1/stats/wordwheel/top-users?season=&language=&page=&pageSize=&includeTest=&excludeStaff=&excludeNoEmail=&q=
     * Signed-in and guest players ordered by current journey level (highest first).
     * Pass season=none for non-season (daily catalog) puzzles and the players who played them.
     * {@code q} matches name, email, session id, or a RevenueCat {@code $RCAnonymousID:…} App User ID.
     * When {@code excludeNoEmail} is true, players without an email are omitted.
     */
    topUsers: async ({
        season,
        language = "english",
        page = 0,
        pageSize = 25,
        includeTest = false,
        excludeStaff = true,
        excludeNoEmail = false,
        q = "",
    } = {}) => {
        try {
            const params = {
                page,
                pageSize,
                includeTest: includeTest ? "true" : "false",
                excludeStaff: excludeStaff ? "true" : "false",
                excludeNoEmail: excludeNoEmail ? "true" : "false",
            };
            if (language) params.language = language;
            if (season) params.season = season;
            const query = String(q || "").trim();
            if (query) {
                // ApiService.getData concatenates query values without encoding; `$`, `:`,
                // spaces, and `…` in a RevenueCat id or copied player label would break GET.
                params.q = encodeURIComponent(query);
            }
            return await ApiService.getData("/v1/stats/wordwheel/top-users", params);
        } catch (error) {
            Logger.error("Error fetching word wheel top users:", error);
            throw error;
        }
    },

    /**
     * GET /v1/stats/wordwheel/daily-puzzles?language=&days=
     * Daily catalog titles for today and the next few days (Montreal rotation).
     */
    dailyPuzzles: async ({ language = "english", days = 3 } = {}) => {
        try {
            const params = {};
            if (language) params.language = language;
            if (days) params.days = days;
            return await ApiService.getData("/v1/stats/wordwheel/daily-puzzles", params);
        } catch (error) {
            Logger.error("Error fetching word wheel daily puzzles:", error);
            throw error;
        }
    },

    /**
     * GET /v1/stats/wordwheel/promotion-users?language=&page=&pageSize=&includeTest=&excludeStaff=&excludeNoEmail=&keyword=
     * Signed-in Word Wheel Quest app players with play counts and first/last play times.
     */
    promotionUsers: async ({
        language = "english",
        page = 0,
        pageSize = 25,
        includeTest = false,
        excludeStaff = true,
        excludeNoEmail = false,
        keyword = "",
    } = {}) => {
        try {
            const params = {
                page,
                pageSize,
                includeTest: includeTest ? "true" : "false",
                excludeStaff: excludeStaff ? "true" : "false",
                excludeNoEmail: excludeNoEmail ? "true" : "false",
            };
            if (language) params.language = language;
            if (keyword) params.keyword = keyword;
            return await ApiService.getData("/v1/stats/wordwheel/promotion-users", params);
        } catch (error) {
            Logger.error("Error fetching word wheel promotion users:", error);
            throw error;
        }
    },
};

export default StatsWordWheelService;
